/**
 * Create or edit an expense report. Every report belongs to a client and
 * optionally one of its projects — both Project Hub's own records.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/context/ToastContext";
import { useProjects } from "@/hooks/useData";
import { pbError, useReportMutations } from "@/hooks/useExpenses";
import { resolveClient, useClients } from "@/hooks/useTime";
import { autoReportName, dayOf } from "@/lib/expenses";
import type { Expense, ExpenseReport } from "@/lib/types";

import { ClientNotes, Modal, Segmented, dangerBtn, ghostBtn, input, label, primaryBtn } from "./ui";

export function ReportForm({ report, expenses = [], onClose }: {
  /** Absent for a new report. */
  report?: ExpenseReport;
  /** The report's expenses, so Delete can say how many go with it. */
  expenses?: Expense[];
  onClose: () => void;
}) {
  const { data: clients = [] } = useClients();
  const { data: projects = [] } = useProjects();
  const { saveReport, deleteReport } = useReportMutations();
  const toast = useToast();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<Partial<ExpenseReport>>(() => report
    ? { ...report, start_date: dayOf(report.start_date), end_date: dayOf(report.end_date) }
    : { type: "trip", client: clients[0]?.id });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = (patch: Partial<ExpenseReport>) => setDraft((d) => ({ ...d, ...patch }));

  const clientId = draft.client || clients[0]?.id || "";
  const client = clients.find((c) => c.id === clientId);
  // Linked by relation or by name — resolveClient is how the rest of Project Hub decides.
  const clientProjects = projects.filter((p) =>
    p.kind !== "inbox" && (p.status !== "archived" || p.id === report?.project) && resolveClient(p, clients)?.id === clientId);

  if (!report && clients.length === 0) {
    return (
      <Modal title="New report" onClose={onClose}>
        <p style={{ fontSize: 13, color: "var(--ink3)", margin: "0 0 16px" }}>
          Every report belongs to a client, and there are none yet.
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <Link to="/clients" style={{ ...primaryBtn, textDecoration: "none" }}>Add a client</Link>
        </div>
      </Modal>
    );
  }

  async function save() {
    if (!clientId) { toast("Pick a client"); return; }
    const data: Partial<ExpenseReport> = {
      name: draft.name?.trim() || autoReportName(client?.name ?? "", draft.start_date),
      client: clientId,
      // A project that belongs to another client would be a silent mismatch.
      project: clientProjects.some((p) => p.id === draft.project) ? draft.project : "",
      type: draft.type ?? "trip",
      start_date: draft.start_date || "",
      end_date: draft.end_date || "",
    };
    if (report) data.invoice_number = (draft.invoice_number ?? "").trim();
    try {
      const saved = await saveReport.mutateAsync(report ? { id: report.id, ...data } : data);
      toast(report ? "Report saved" : "Report created");
      onClose();
      if (!report) navigate(`/expenses/${saved.id}`);
    } catch (e) {
      toast("Couldn't save: " + pbError(e));
    }
  }

  return (
    <Modal title={report ? "Edit report" : "New report"} onClose={onClose}>
      <label style={label}>
        Client
        <select value={clientId} onChange={(e) => set({ client: e.target.value, project: "" })} style={input}>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </label>

      <div style={{ margin: "-4px 0 12px" }}><ClientNotes client={client} compact /></div>

      <label style={label}>
        Project
        <select value={draft.project ?? ""} onChange={(e) => set({ project: e.target.value })} style={input}>
          <option value="">— None</option>
          {clientProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {clientProjects.length === 0 && (
          <span style={{ display: "block", fontSize: 11, color: "var(--muted2)", marginTop: 5 }}>
            {client?.name ?? "This client"} has no projects linked yet.
          </span>
        )}
      </label>

      <div style={label}>
        Type
        <div><Segmented value={draft.type ?? "trip"} options={[["trip", "Trip"], ["month", "Month"]]} onChange={(v) => set({ type: v })} /></div>
      </div>

      <label style={label}>
        Report name
        <input value={draft.name ?? ""} onChange={(e) => set({ name: e.target.value })} style={input}
          placeholder={autoReportName(client?.name ?? "", draft.start_date)} />
        <span style={{ display: "block", fontSize: 11, color: "var(--muted2)", marginTop: 5 }}>Leave blank to auto-name from the client and start date.</span>
      </label>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <label style={label}>
          Start
          <input type="date" value={draft.start_date ?? ""} onChange={(e) => set({ start_date: e.target.value })} style={input} />
        </label>
        <label style={label}>
          End
          <input type="date" value={draft.end_date ?? ""} onChange={(e) => set({ end_date: e.target.value })} style={input} />
        </label>
      </div>

      {report && (
        <label style={label}>
          Invoice number
          <input value={draft.invoice_number ?? ""} onChange={(e) => set({ invoice_number: e.target.value })} style={input} placeholder="Optional" />
        </label>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        {report && <button onClick={() => setConfirmDelete(true)} style={dangerBtn}>Delete report</button>}
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={ghostBtn}>Cancel</button>
        <button onClick={() => void save()} style={primaryBtn} disabled={saveReport.isPending}>
          {report ? "Save changes" : "Create report"}
        </button>
      </div>

      {confirmDelete && report && (
        <ConfirmDialog
          title={`Delete “${report.name}”?`}
          message={expenses.length
            ? `This also deletes its ${expenses.length} expense${expenses.length === 1 ? "" : "s"} and their receipts, on every device. It cannot be undone.`
            : "It has no expenses. This cannot be undone."}
          onConfirm={async () => {
            try {
              await deleteReport.mutateAsync({ id: report.id, expenseIds: expenses.map((e) => e.id) });
              toast("Report deleted");
              onClose();
              navigate("/expenses");
            } catch (e) {
              toast("Couldn't delete: " + pbError(e));
            }
          }}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </Modal>
  );
}
